import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import SwalAlertComp from "../../../components/SwalAlert";
import { baseURL } from "../../../configurations/environment";
import { TestEventsListType } from "../../../enums/test-events";
import { RequestConfig } from "../../../helpers/RequestConfig";
import {
    buildDevMockTestEventDetail,
    buildDevMockTestEventsOrRequestsResponse,
    withDevMockTestEventDetail,
    withDevMockTestEvents,
} from "./testEvents.mock";

export const getTrackerTestEventCodeAsync = createAsyncThunk(
    "testEvents/getTrackerTestEventCode",
    async (_, thunkAPI) => {
        const {
            testEvents: { trackerId },
        } = thunkAPI.getState();
        try {
            let requestURL =
                baseURL + `/api/trackers/${trackerId}/test-event-code`;

            const response = await axios.get(requestURL, RequestConfig());
            thunkAPI.dispatch(getTestEventsOrRequestsAsync());
            return response.data.data?.test_event_code;
        } catch (error) {
            SwalAlertComp({
                title: "Failed",
                message: error?.message || "Failed to get test event code",
                icon: "error",
            });
        }
    },
);

export const postTrackerTestEventCodeAsync = createAsyncThunk(
    "testEvents/postTrackerTestEventCode",
    async (payload, thunkAPI) => {
        const {
            testEvents: { trackerId },
        } = thunkAPI.getState();

        try {
            let requestURL = baseURL + `/api/trackers/test-event-code`;

            const response = await axios.post(
                requestURL,
                {
                    tracker_id: trackerId,
                    ...payload,
                },
                RequestConfig(),
            );
            SwalAlertComp({
                title: "Success",
                message: "Test event code created successfully",
                icon: "success",
                timer: 3000,
            });
            thunkAPI.dispatch(getTrackerTestEventCodeAsync());
            thunkAPI.dispatch(getTestEventsOrRequestsAsync());
            return response.data?.data;
        } catch (error) {
            SwalAlertComp({
                title: "Failed",
                message:
                    error?.response?.data?.message ||
                    "Failed to create test event code",
                icon: "error",
                timer: 3000,
            });
        }
    },
);

export const putTrackerTestEventCodeAsync = createAsyncThunk(
    "testEvents/putTrackerTestEventCode",
    async ({ ...payload }, thunkAPI) => {
        try {
            const {
                testEvents: {
                    trackerTestEventCode: { data },
                },
            } = thunkAPI.getState();

            let requestURL =
                baseURL + `/api/trackers/test-event-code/${data?.id}`;
            const response = await axios.put(
                requestURL,
                payload,
                RequestConfig(),
            );
            SwalAlertComp({
                title: "Success",
                message: "Test event code updated successfully",
                icon: "success",
                timer: 3000,
            });
            thunkAPI.dispatch(getTrackerTestEventCodeAsync());
            thunkAPI.dispatch(getTestEventsOrRequestsAsync());
            return response.data?.data;
        } catch (error) {
            SwalAlertComp({
                title: "Failed",
                message:
                    error?.response?.data?.message ||
                    "Failed to update test event code",
                icon: "error",
                timer: 3000,
            });
        }
    },
);

export const deleteTrackerTestEventCodeAsync = createAsyncThunk(
    "testEvents/deleteTrackerTestEventCode",
    async (_, thunkAPI) => {
        try {
            const {
                testEvents: {
                    trackerTestEventCode: { data },
                },
            } = thunkAPI.getState();

            let requestURL =
                baseURL + `/api/trackers/test-event-code/${data?.id}`;
            const response = await axios.delete(requestURL, RequestConfig());
            thunkAPI.dispatch(getTrackerTestEventCodeAsync());
            thunkAPI.dispatch(getTestEventsOrRequestsAsync());
            return response.data?.data;
        } catch (error) {
            SwalAlertComp({
                title: "Failed",
                message:
                    error?.response?.data?.message ||
                    "Failed to delete test event code",
                icon: "error",
                timer: 3000,
            });
        }
    },
);

export const getTestEventDetailAsync = createAsyncThunk(
    "testEvents/getTestEventDetail",
    async (eventId, thunkAPI) => {
        try {
            const {
                testEvents: { trackerId },
            } = thunkAPI.getState();

            let requestURL =
                baseURL +
                `/api/trackers/test-event-code/${trackerId}/result/details?id=${eventId}&type=events`;

            const response = await axios.get(requestURL, RequestConfig());
            return withDevMockTestEventDetail(
                response?.data?.data?.[0]?.detailed_data,
                () => buildDevMockTestEventDetail(eventId),
            );
        } catch (error) {
            // In DEV_MODE the fixtures stand in for the endpoint, so the modal
            // still has something to show instead of an error toast.
            const mock = withDevMockTestEventDetail(null, () =>
                buildDevMockTestEventDetail(eventId),
            );
            if (mock) return mock;

            SwalAlertComp({
                title: "Failed",
                message:
                    error?.response?.data?.message ||
                    "Failed to get test event detail",
                icon: "error",
                timer: 3000,
            });
        }
    },
);

export const getTestEventsOrRequestsAsync = createAsyncThunk(
    "testEvents/getTestEventsOrRequests",
    async (action, thunkAPI) => {
        try {
            const {
                testEvents: { eventsList, requestsList, activeTab },
            } = thunkAPI.getState();
            const page =
                activeTab === TestEventsListType.EVENTS
                    ? eventsList.page
                    : requestsList.page;
            const limit =
                activeTab === TestEventsListType.EVENTS
                    ? eventsList.limit
                    : requestsList.limit;
            const {
                testEvents: { trackerId },
            } = thunkAPI.getState();
            const buildMock = () =>
                buildDevMockTestEventsOrRequestsResponse(
                    activeTab,
                    page,
                    limit,
                );

            if (trackerId) {
                let requestURL =
                    baseURL +
                    `/api/trackers/test-event-code/${trackerId}/result?type=${activeTab}&limit=${limit}&skip=${page}&refresh=1`;
                const response = await axios.get(requestURL, RequestConfig());

                return withDevMockTestEvents(response.data.data, buildMock);
            }

            // No tracker selected yet: still worth filling the tabs in DEV_MODE.
            return withDevMockTestEvents(undefined, buildMock);
        } catch (error) {
            const {
                testEvents: { eventsList, requestsList, activeTab },
            } = thunkAPI.getState();
            // In DEV_MODE the fixtures stand in for the endpoint, so a failing
            // request fills the tabs instead of raising an alert.
            const mock = withDevMockTestEvents(undefined, () =>
                buildDevMockTestEventsOrRequestsResponse(
                    activeTab,
                    activeTab === TestEventsListType.EVENTS
                        ? eventsList.page
                        : requestsList.page,
                    activeTab === TestEventsListType.EVENTS
                        ? eventsList.limit
                        : requestsList.limit,
                ),
            );
            if (mock) return mock;

            SwalAlertComp({
                title: "Failed",
                message:
                    error?.response?.data?.message ||
                    "Failed to get test events or requests",
                icon: "error",
                timer: 3000,
            });
        }
    },
);
